import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, FileText, Loader2, Package } from "lucide-react";
import { downloadGuide } from "@/lib/apiGuide";
import { Logo } from "@/components/site/Logo";
import { appsApi, errMsg, notifyError } from "@/lib/appsApi";
import { useNoIndex } from "@/lib/useNoIndex";
import { useFileActions } from "@/lib/useFileActions";
import { UploadForm } from "@/components/apps/UploadForm";
import { UnitCard } from "@/components/bundles/UnitCard";
import { AppPills, AddForm, AppSettings } from "@/components/bundles/BundleControls";

const selectCls = "rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-ink focus:border-sky-600 focus:outline-none";

export default function BundlesPage() {
  useNoIndex();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [apps, setApps] = useState([]);
  const [files, setFiles] = useState([]);
  const [active, setActive] = useState("");
  const [tabs, setTabs] = useState([]);
  const [newTab, setNewTab] = useState("");
  const [keys, setKeys] = useState({});
  const [loaded, setLoaded] = useState(false);
  const isAdmin = user?.role === "admin";

  const load = useCallback(async () => {
    try {
      const r = await appsApi.get("/bundles");
      const list = Array.isArray(r?.data) ? r.data : [];
      setApps(list);
      setFiles(list.flatMap((a) => (Array.isArray(a.packages) ? a.packages : [])));
      setActive((cur) => (list.some((a) => a.id === cur) ? cur : list[0]?.id ?? ""));
    } catch (err) {
      notifyError(errMsg(err, "Loading the data packages"));
    } finally {
      setLoaded(true);
    }
  }, []);

  useEffect(() => {
    document.title = "Data packages | Aero Soft Solutions";
    appsApi.get("/auth/me").then((r) => setUser(r?.data ?? null)).catch(() => navigate("/apps"));
  }, [navigate]);

  useEffect(() => {
    if (!user) return undefined;
    load();
    if (user.role === "admin") appsApi.get("/tabs").then((r) => { const t = Array.isArray(r?.data) ? r.data : []; setTabs(t); setNewTab(t[0]?.id ?? ""); }).catch(() => setTabs([]));
    return undefined;
  }, [user, load]);

  const processing = files.some((f) => f.status === "processing");
  useEffect(() => {
    if (!processing) return undefined;
    const timer = setInterval(load, 2000);
    return () => clearInterval(timer);
  }, [processing, load]);

  const app = apps.find((a) => a.id === active);

  const actions = useFileActions({ setFiles, load });

  const call = async (fn, action, done) => {
    try {
      const r = await fn();
      if (done) done(r?.data);
      await load();
      return true;
    } catch (err) {
      notifyError(errMsg(err, action));
      return false;
    }
  };

  const addApp = (name) => call(() => appsApi.post("/admin/bundles", { name, tab_id: newTab }), "Adding the app", (d) => { setActive(d?.id ?? ""); toast.success(`Added ${name}`); });
  const addUnit = (name) => call(() => appsApi.post(`/admin/bundles/${app.id}/units`, { name }), "Adding the unit", (d) => {
    if (d?.id) setKeys((k) => ({ ...k, [`${app.id}/${d.id}`]: d.code }));
    toast.success(`Added ${name}. Give the team its activation code.`);
  });
  // Used by the upload form when a Deployment package names a unit that isn't here yet.
  const addUnitFor = async ({ name, group_id }) => {
    try {
      const r = await appsApi.post(`/admin/bundles/${app.id}/units`, { name, group_id });
      setKeys((k) => ({ ...k, [`${app.id}/${r.data.id}`]: r.data.code }));
      toast.success(`Added unit ${name}. Its activation code is on its card.`);
      await load();
      return r.data.id;
    } catch (err) {
      notifyError(errMsg(err, `Adding unit ${name}`));
      return null;
    }
  };
  const deleteUnit = (unit) => call(() => appsApi.delete(`/admin/bundles/${app.id}/units/${unit.id}`), "Removing the unit", () => toast.success(`Removed ${unit.name}`));
  const saveApp = (body) => call(() => appsApi.patch(`/admin/bundles/${app.id}`, body), "Updating the app", () => toast.success("Saved"));
  const deleteApp = (a) => call(() => appsApi.delete(`/admin/bundles/${a.id}`), "Removing the app", () => toast.success(`Removed ${a.name}`));

  if (!user) return <div className="sky-wash flex min-h-screen items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-sky-600" /></div>;

  return (
    <div className="sky-wash min-h-screen" data-testid="bundles-page">
      <div className="mx-auto max-w-5xl px-5 py-10 sm:px-6">
        <div className="flex items-center justify-between gap-4">
          <Logo />
          <div className="flex flex-wrap justify-end gap-2">
            {isAdmin && (
              <button onClick={() => downloadGuide(apps)} className="inline-flex items-center gap-2 rounded-full bg-ink px-4 py-2 text-sm text-white hover:bg-sky-700" data-testid="bundles-guide-download"><FileText className="h-4 w-4" />Developer guide</button>
            )}
            <button onClick={() => navigate("/apps")} className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2 text-sm text-ink hover:border-sky-600" data-testid="bundles-back-button"><ArrowLeft className="h-4 w-4" />Back</button>
          </div>
        </div>
        <p className="mt-12 font-mono text-xs uppercase tracking-[0.25em] text-sky-700">{isAdmin ? "Admin" : "Tester"} · {user.email}</p>
        <h1 className="mt-3 font-display text-4xl font-semibold tracking-[-0.03em] text-ink sm:text-5xl">Data <span className="font-serif font-normal italic text-sky-600">packages</span></h1>
        <p className="mt-4 max-w-2xl text-sm leading-relaxed text-slate-600 md:text-base">Packages for each app, kept per unit. Every version is kept; the newest visible one is what the app downloads.</p>

        <div className="mt-8 space-y-4">
          {loaded && apps.length === 0 && <p className="rounded-3xl border border-dashed border-sky-200 bg-white p-10 text-center text-slate-600" data-testid="bundles-empty">{isAdmin ? "No apps yet. Add one below." : "No data packages have been shared with you yet."}</p>}
          <AppPills apps={apps} active={active} onSelect={setActive} />
          {isAdmin && app && <AppSettings app={app} tabs={tabs} onSave={saveApp} onDelete={deleteApp} />}
        </div>

        {app && (
          <div className="mt-8 space-y-5" data-testid={`bundle-app-panel-${app.id}`}>
            {app.units.length === 0 && <p className="rounded-3xl border border-dashed border-sky-200 bg-white p-8 text-center text-slate-600" data-testid="bundle-units-empty">{isAdmin ? `${app.name} has no units yet. Add the first one below.` : `No packages for ${app.name} yet.`}</p>}
            {app.units.map((u) => (
              <UnitCard key={u.id} app={app} unit={{ ...u, code: keys[`${app.id}/${u.id}`] }} packages={files.filter((f) => f.bundle_app === app.id && (f.unit_ids ?? []).includes(u.id))}
                isAdmin={isAdmin} actions={actions} onDeleteUnit={deleteUnit} onChanged={async () => { setKeys((k) => { const n = { ...k }; delete n[`${app.id}/${u.id}`]; return n; }); await load(); }} />
            ))}
          </div>
        )}

        {isAdmin && (
          <div className="mt-10 grid gap-6 lg:grid-cols-2">
            {app ? (
              <UploadForm key={app.id} tabs={tabs} fileTypes={[]} existing={[]}
                pkg={{ appId: app.id, appName: app.name, tabId: app.tab_id, units: app.units, addUnit: addUnitFor }}
                onUploaded={(f) => { if (f) setFiles((l) => [f, ...l]); }} />
            ) : <div />}
            <div className="space-y-6">
              {app && (
                <div className="rounded-[28px] border border-sky-100 bg-white p-6 sm:p-8">
                  <h3 className="font-display text-xl font-semibold text-ink">Add a unit to {app.name}</h3>
                  <p className="mt-1 text-sm text-slate-600">Each unit (team) gets its own packages and a one-time activation code for its app.</p>
                  <div className="mt-4"><AddForm placeholder="Unit name, e.g. 1 Squadron" button="Add unit" onAdd={addUnit} testid="bundle-add-unit" /></div>
                </div>
              )}
              <div className="rounded-[28px] border border-sky-100 bg-white p-6 sm:p-8">
                <h3 className="flex items-center gap-2 font-display text-xl font-semibold text-ink"><Package className="h-5 w-5 text-sky-600" />Add another app</h3>
                <p className="mt-1 text-sm text-slate-600">Testers in the chosen tab can download its packages by hand.</p>
                <div className="mt-4">
                  <AddForm placeholder="App name" button="Add app" onAdd={addApp} testid="bundle-add-app">
                    <select value={newTab} onChange={(e) => setNewTab(e.target.value)} className={selectCls} aria-label="Tester tab" data-testid="bundle-add-app-tab">
                      {tabs.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                    </select>
                  </AddForm>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
